import { Columns } from '@shared/interfaces/table/column-config';

/**
 * Reorders the given array of columns based on the provided key order.
 * Columns not included in the key order will retain their relative order
 * and be appended at the end of the reordered array.
 *
 * @param {Columns<T>[]} Columns - The array of column objects to reorder.
 * @param {string[]} keyOrder - An array of keys defining the desired order of columns. Keys should match those in the column objects.
 * @return {Columns<T>[]} A new array of columns, reordered according to the specified key order, with remaining columns appended.
 */
export function reorderColumnsByKeys<T>(
  Columns: Columns<T>[],
  keyOrder: string[]
): Columns<T>[] {
  const keyToColumn = new Map<string, Columns<T>>();

  // Convert keys (even if NestedKeyOf<T>) to string for matching
  for (const col of Columns) {
    const keys = Array.isArray(col.key) ? col.key : [col.key];
    const keyString = keys.join('.'); // assumes nested keys are dot-separated
    keyToColumn.set(keyString, col);
  }

  // Reorder based on keyOrder
  const ordered: Columns<T>[] = keyOrder
    .map(key => keyToColumn.get(key))
    .filter((col): col is Columns<T> => !!col); // Remove undefined

  // Append any remaining Columns that weren't in the order list
  const unordered = Columns.filter(col => {
    const keyString = (Array.isArray(col.key) ? col.key : [col.key]).join('.');
    return !keyOrder.includes(keyString);
  });

  return [...ordered, ...unordered];
}
